import { Suspense, useEffect } from 'react'
import { lazyRetry } from '@/lib/lazyRetry'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createUserBoundPersister, shouldPersistQuery, QUERY_CACHE_MAX_AGE_MS } from '@/lib/queryPersist'
import { Toaster } from '@/components/ui/toaster'
import { UploadingOverlay } from '@/lib/uploadOverlay'
import { TooltipProvider } from '@/components/ui/tooltip'
import { GamificationProvider } from '@/hooks/useGamification'
import { GuestModeProvider, useGuestMode } from '@/hooks/useGuestMode'
import { ThemeProvider } from '@/hooks/useTheme'
import { useAuth, canAccessAdmin, isFullAdmin } from '@/hooks/useAuth'
import { missingFields } from "@/lib/profileCompleteness"
import { stashPendingRoute, takePendingRoute } from '@/lib/pendingRoute'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { AppReadySignal, markAppShellErrored } from '@/lib/appReady'
import ErrorRetry from '@/components/ErrorRetry'
import RegisterPrompt from '@/components/RegisterPrompt'
import OfflineBanner from '@/components/OfflineBanner'
import DownloadAppBanner from '@/components/DownloadAppBanner'
import AnalyticsConsentBanner from '@/components/AnalyticsConsentBanner'
import { VersionUpdateBanner } from '@/components/VersionUpdateBanner'
import MinimumVersionGate from '@/components/MinimumVersionGate'
import OfflineSyncDropListener from '@/components/OfflineSyncDropListener'
import { ActiveWorkoutSticky } from '@/components/ActiveWorkoutSticky'
import ActiveMissionBar from '@/components/ActiveMissionBar'
import { usePageTracking } from '@/hooks/usePageTracking'
import { useDeepLink } from "@/hooks/useDeepLink";
import { captureReferralFromSearch } from "@/lib/referralCapture";
import { queryClient } from '@/lib/queryClient'

// Lazy-loaded feature pages
// NOTE: MainApp (the home shell) was eager-imported, which pulled it + its
// static dependencies into the main `index` chunk — the bundle the WebView
// must download + parse before the first frame on a cold start. Lazy-loading
// it shrinks that critical chunk; the home chunk then loads from the on-device
// Capgo bundle (local read, no network) the moment the auth gate clears, in
// parallel with the route resolving. (Founder 7s cold-start, 2026-06-14.)
const MainApp = lazyRetry(() => import('@/pages/MainApp'))
// LoginPage was eager too (~26 KB minified in the main chunk). It's only
// needed once someone taps Sign up / Log in — an ad visitor lands on the
// Gateway first — and every route that renders it sits inside a Suspense.
// (2026-10-08)
const LoginPage = lazyRetry(() => import('@/pages/LoginPage'))
const ProfilePage = lazyRetry(() => import('@/pages/ProfilePage'))
const MessagesPage = lazyRetry(() => import('@/pages/MessagesPage'))
const NotificationsPage = lazyRetry(() => import('@/pages/NotificationsPage'))
const AboutPage = lazyRetry(() => import('@/pages/AboutPage'))
const BrandPage = lazyRetry(() => import('@/pages/BrandPage'))
const TermsPage = lazyRetry(() => import('@/pages/TermsPage'))
const PrivacyPage = lazyRetry(() => import('@/pages/PrivacyPage'))
const FAQPage = lazyRetry(() => import('@/pages/FAQPage'))
const SupportPage = lazyRetry(() => import('@/pages/SupportPage'))
const AdminDashboard = lazyRetry(() => import('@/pages/AdminDashboard'))
const AdminMobilityClips = lazyRetry(() => import('@/pages/AdminMobilityClips'))
const AdminYogaClips = lazyRetry(() => import('@/pages/AdminYogaClips'))
const AdminNarration = lazyRetry(() => import('@/pages/AdminNarration'))
const AdminMotivation = lazyRetry(() => import('@/pages/AdminMotivation'))
const AdminConcierge = lazyRetry(() => import('@/pages/AdminConcierge'))
const SharedEvent = lazyRetry(() => import('@/pages/SharedEvent'))
const SharedDuel = lazyRetry(() => import('@/pages/SharedDuel'))
const SharedWorkout = lazyRetry(() => import('@/pages/SharedWorkout'))
const PublicProfile = lazyRetry(() => import('@/pages/PublicProfile'))
const Landing = lazyRetry(() => import('@/pages/Landing'))
const Gateway = lazyRetry(() => import('@/pages/Gateway'))
const NotFound = lazyRetry(() => import('@/pages/not-found'))
const PitchDeck = lazyRetry(() => import('@/pages/PitchDeck'))
const InvestorDeck = lazyRetry(() => import('@/pages/InvestorDeck'))
const WhitePaper = lazyRetry(() => import('@/pages/WhitePaper'))
const SeventyFiveHardInfo = lazyRetry(() => import('@/pages/SeventyFiveHardInfo'))
const ChallengesLanding = lazyRetry(() => import('@/pages/ChallengesLanding'))
const WhyTrybeUP = lazyRetry(() => import('@/pages/WhyTrybeUP'))
const RewardZone = lazyRetry(() => import('@/pages/RewardZone'))
const MyWorkouts = lazyRetry(() => import('@/pages/MyWorkouts'))
const MySobriety = lazyRetry(() => import("./pages/MySobriety"));
const ResetPasswordPage = lazyRetry(() => import('@/pages/ResetPasswordPage'))
const IdentityNext = lazyRetry(() => import('@/pages/IdentityNext'))
const IdentityUnlockGate = lazyRetry(() => import('@/components/IdentityUnlockGate'))
const BadgeToastListener = lazyRetry(() => import('@/hooks/useBadgeToasts'))

function LazyFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  )
}

/**
 * PageTrackingHandler — fires a page_view event on every route change.
 * Must be inside BrowserRouter so useLocation works.
 */
function PageTrackingHandler() {
  usePageTracking()
  return null
}

/**
 * DeepLinkHandler — routes native App/Universal Link opens into React Router.
 * Must be inside BrowserRouter so useNavigate works. Found 2026-08-05: the
 * useDeepLink hook existed but was mounted NOWHERE, so Android App Links
 * (which the manifest claims for trybeup.com) opened the app on whatever
 * screen it was last on and dropped the URL entirely.
 */
function DeepLinkHandler() {
  useDeepLink()
  return null
}

/**
 * ReferralCaptureHandler — stores `?ref=CODE` from ANY entry route (review
 * 2026-09-14). Referral links point at `/`, which renders the Gateway, and
 * only /landing ever read the param — so every shared link silently lost its
 * code. Runs on each location change so native deep links are covered too.
 */
function ReferralCaptureHandler() {
  const location = useLocation()
  useEffect(() => {
    captureReferralFromSearch(location.search)
  }, [location.search])
  return null
}

/**
 * PendingRouteHandler — replays a deep-link destination that was stashed
 * before the auth gate had resolved.
 *
 * Mounted inside AuthenticatedApp, so by the time it runs we have a session
 * and the destination's route actually exists. Two sources:
 *   • the `trybeup:navigate` event — a push tapped while the app is already
 *     open and mounted;
 *   • the durable stash — a push tapped on a COLD start (the click listener
 *     fires while the gate is still a spinner, so no listener exists yet), or
 *     an attempt that got bounced to /login by the unauthenticated catch-all.
 *
 * Without this, the Concierge push reloaded the WebView, lost the auth race,
 * and left the founder on the sign-in screen with nowhere to go back to
 * (report 2026-08-12).
 */
function PendingRouteHandler() {
  const navigate = useNavigate()
  useEffect(() => {
    const go = (path: string | null) => {
      if (path) navigate(path, { replace: true })
    }
    // Cold-start / post-login case: whatever was stashed before we got here.
    go(takePendingRoute())
    // Warm case: the app is already mounted when the push is tapped.
    const onNavigate = () => go(takePendingRoute())
    window.addEventListener('trybeup:navigate', onNavigate)
    return () => window.removeEventListener('trybeup:navigate', onNavigate)
  }, [navigate])
  return null
}

/**
 * StashAndLogin — the unauthenticated catch-all.
 *
 * Still redirects to /login, but remembers where the user was trying to go
 * first. `<Navigate replace>` overwrites the URL, so without the stash the
 * destination is gone the moment this renders — which is how a Concierge push
 * that merely raced the auth gate turned into a dead-end sign-in screen. With
 * it, a lost race costs a moment (PendingRouteHandler replays it), and a
 * genuinely signed-out user lands where they meant to go after signing in.
 */
function StashAndLogin() {
  const location = useLocation()
  stashPendingRoute(location.pathname + location.search)
  return <Navigate to="/login" replace />
}

/**
 * AdminRoute — guards the /admin route. Full admins get everything; a
 * scoped marketing role is allowed in (the dashboard itself filters which
 * tabs they see, and the API enforces what they can read). Everyone else
 * is redirected home.
 */
/**
 * Standalone authenticated routes (outside MainApp). The server-side profile
 * gate 403s every API call for an incomplete account — MainApp overlays the
 * blocking ProfileGate card for that state, but these routes used to render
 * raw errors with no card and no explanation (audit 2026-08-04: push deep
 * links, /profile itself, bookmarks). Redirecting to "/" lands the user in
 * MainApp, where the card is.
 */
function GatedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return <LazyFallback />
  if (user && missingFields(user).length > 0) return <Navigate to="/" replace />
  return <>{children}</>
}

function AdminRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <LazyFallback />
  if (!canAccessAdmin(user)) return <Navigate to="/" replace />
  return <AdminDashboard />
}

function AdminMobilityRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <LazyFallback />
  if (!canAccessAdmin(user)) return <Navigate to="/" replace />
  return <AdminMobilityClips />
}

function AdminYogaRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <LazyFallback />
  if (!canAccessAdmin(user)) return <Navigate to="/" replace />
  return <AdminYogaClips />
}

function AdminNarrationRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <LazyFallback />
  if (!canAccessAdmin(user)) return <Navigate to="/" replace />
  return <AdminNarration />
}

function AdminMotivationRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <LazyFallback />
  if (!canAccessAdmin(user)) return <Navigate to="/" replace />
  return <AdminMotivation />
}

function AdminConciergeRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <LazyFallback />
  // Full-admin only — matches the admin feature-request endpoints' gate
  // (is_staff_admin), so marketing can't land on a 403/empty inbox.
  if (!isFullAdmin(user)) return <Navigate to="/" replace />
  return <AdminConcierge />
}

/**
 * TrybeRedirect — stores pending trybe/invite info and redirects to app root.
 */
function TrybeRedirect() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const inviteCode = searchParams.get('invite')

    if (inviteCode) {
      localStorage.setItem(
        'pendingTrybeInvite',
        JSON.stringify({ trybeId: id, inviteCode })
      )
    } else {
      localStorage.setItem('pendingTrybeId', id || '')
    }

    navigate('/', { replace: true })
  }, [id, navigate, location.search])

  return null
}

/**
 * GroupFastRedirect — handles /group-fast/:id?key=CODE share links for
 * collective (group) fasts. Stores the pending fast (+ optional invite key)
 * and redirects to the app root so MainApp can open the Fasting → Group tab
 * on that fast once the user is authenticated.
 */
function GroupFastRedirect() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const key = new URLSearchParams(location.search).get('key') || undefined
    if (id) {
      localStorage.setItem('pendingGroupFast', JSON.stringify({ id, key }))
    }
    navigate('/', { replace: true })
  }, [id, navigate, location.search])

  return null
}

/**
 * The66TrybeRedirect — handles /the-66-trybe/:id?key=CODE share links for The 66
 * Trybes. Stores the pending Trybe (+ optional invite key) and redirects to the
 * app root so MainApp can open the Challenges tab on that Trybe once authed.
 */
function The66TrybeRedirect() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const key = new URLSearchParams(location.search).get('key') || undefined
    if (id) {
      localStorage.setItem('pendingThe66Trybe', JSON.stringify({ id, key }))
    }
    navigate('/', { replace: true })
  }, [id, navigate, location.search])

  return null
}

/**
 * JoinRedirect — handles /join?invite=CODE links sent in Trybe invite emails.
 * Stores the invite code in localStorage and redirects to the app root so
 * MainApp can auto-join the Trybe once the user is authenticated.
 */
function JoinRedirect() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const inviteCode = searchParams.get('invite')

    if (inviteCode) {
      localStorage.setItem('pendingJoinInviteCode', inviteCode)
    }

    navigate('/', { replace: true })
  }, [navigate, location.search])

  return null
}

/** Shared static/public routes */
function SharedRoutes() {
  return (
    <>
      <Route path="/about" element={<AboutPage />} />
      <Route path="/brand" element={<BrandPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contact" element={<Navigate to="/support" replace />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/why" element={<WhyTrybeUP />} />
      <Route path="/trybes/:id" element={<TrybeRedirect />} />
      <Route path="/group-fast/:id" element={<GroupFastRedirect />} />
      <Route path="/the-66-trybe/:id" element={<The66TrybeRedirect />} />
      <Route path="/join" element={<JoinRedirect />} />
      <Route path="/profile/:userId" element={<PublicProfile />} />
      <Route path="/events/:eventId" element={<SharedEvent />} />
      <Route path="/duels/:duelId" element={<SharedDuel />} />
      <Route path="/workouts/:workoutId" element={<SharedWorkout />} />
      <Route path="/landing" element={<Landing />} />
    </>
  )
}

/** Routes for authenticated users */
function AuthenticatedApp() {
  return (
    <Suspense fallback={<LazyFallback />}>
      {/* Identity stage advancement celebration — renders nothing unless
          the user has just crossed into a new stage since they last visited
          on this device. See IdentityUnlockGate for the detection logic. */}
      <IdentityUnlockGate />
      {/* Badge-earned toasts — listens for new IDs on user.badges and fires
          a celebratory toast. Silent on first load. */}
      <BadgeToastListener />
      {/* Replays a push/deep-link destination that arrived before the auth
          gate resolved — see the component for the Concierge case. */}
      <PendingRouteHandler />
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/app" element={<MainApp />} />
        <Route path="/profile" element={<GatedRoute><ProfilePage /></GatedRoute>} />
        <Route path="/messages" element={<GatedRoute><MessagesPage /></GatedRoute>} />
        <Route path="/notifications" element={<GatedRoute><NotificationsPage /></GatedRoute>} />
        <Route path="/admin" element={<AdminRoute />} />
        <Route path="/admin/mobility" element={<AdminMobilityRoute />} />
        <Route path="/admin/yoga" element={<AdminYogaRoute />} />
        <Route path="/admin/narration" element={<AdminNarrationRoute />} />
        <Route path="/admin/motivation" element={<AdminMotivationRoute />} />
        <Route path="/admin/concierge" element={<AdminConciergeRoute />} />
        <Route path="/pitch" element={<PitchDeck />} />
        <Route path="/pitch/deck" element={<InvestorDeck />} />
        <Route path="/white-paper" element={<WhitePaper />} />
        <Route path="/75-hard" element={<SeventyFiveHardInfo />} />
        <Route path="/the-66" element={<SeventyFiveHardInfo />} />
        <Route path="/challenges" element={<ChallengesLanding />} />
        <Route path="/workout" element={<GatedRoute><MyWorkouts /></GatedRoute>} />
        {/* Sobriety as a real URL — linkable and bookmarkable, which a tab or a
            sheet can never be. Same panel as the in-app surfaces. */}
        <Route path="/sobriety" element={<GatedRoute><MySobriety /></GatedRoute>} />
        <Route path="/rewards" element={<GatedRoute><RewardZone /></GatedRoute>} />
        <Route path="/identity/next" element={<GatedRoute><IdentityNext /></GatedRoute>} />
        {SharedRoutes()}
        <Route path="/login" element={<Navigate to="/app" replace />} />
        <Route path="/" element={<MainApp />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

/** Routes for guest mode (preview without login) */
function GuestApp() {
  return (
    <>
      <RegisterPrompt />
      <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/app" element={<MainApp />} />
          <Route path="/white-paper" element={<WhitePaper />} />
        <Route path="/75-hard" element={<SeventyFiveHardInfo />} />
        <Route path="/the-66" element={<SeventyFiveHardInfo />} />
        <Route path="/challenges" element={<ChallengesLanding />} />
          <Route path="/workout" element={<MyWorkouts />} />
          <Route path="/sobriety" element={<MySobriety />} />
          <Route path="/rewards" element={<RewardZone />} />
          {SharedRoutes()}
          <Route path="/" element={<MainApp />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  )
}

/** Routes for unauthenticated users */
function UnauthenticatedApp() {
  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/white-paper" element={<WhitePaper />} />
        <Route path="/75-hard" element={<SeventyFiveHardInfo />} />
        <Route path="/the-66" element={<SeventyFiveHardInfo />} />
        <Route path="/challenges" element={<ChallengesLanding />} />
        <Route path="/workout" element={<MyWorkouts />} />
        {SharedRoutes()}
        <Route path="/" element={<Gateway />} />
        {/* Any protected path → send to login, remembering where they were
            headed so the destination survives (see StashAndLogin). */}
        <Route path="*" element={<StashAndLogin />} />
      </Routes>
    </Suspense>
  )
}

function Router() {
  const { isAuthenticated, isLoading, isPasswordRecovery, isSuspended, isUserError, refetchUser } = useAuth()
  const { isGuest } = useGuestMode()

  // While GoTrue resolves the session (or exchanges a PKCE code), show a
  // spinner. DO NOT render UnauthenticatedApp here — its catch-all
  // <Navigate to="/login" replace /> would fire synchronously and strip the
  // ?code= query param from window.location before GoTrueClient can exchange
  // the PKCE code.
  if (isLoading) {
    return <LazyFallback />
  }

  // Account suspended — show a clear message instead of confusing errors
  if (isAuthenticated && isSuspended) {
    return (
      <div className="min-h-screen bg-[#1a1408] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-lg font-semibold text-[#e8d5b0]">Account Suspended</h2>
        <p className="mt-2 text-sm text-[#e8d5b0]/60 max-w-xs">
          Your account has been suspended. If you think this is a mistake, please contact us at{' '}
          <a href="mailto:hello@trybeup.com" className="text-[#d4a017] hover:underline">hello@trybeup.com</a>.
        </p>
        <button
          onClick={async () => {
            const { signOut } = await import('@/lib/auth')
            await signOut().catch(() => {})
            window.location.href = '/'
          }}
          className="mt-6 rounded-lg border border-[#d4a017]/25 px-6 py-2 text-sm font-medium text-[#e8d5b0] hover:border-[#d4a017]/50 transition"
        >
          Log Out
        </button>
      </div>
    )
  }

  // Session exists but profile fetch failed (network error, server down, etc.)
  // Show a retry screen instead of bouncing to login
  if (isAuthenticated && isUserError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <ErrorRetry message="Couldn't load your profile" onRetry={() => refetchUser()} />
      </div>
    )
  }

  // Supabase fired PASSWORD_RECOVERY — send user to the reset form
  if (isPasswordRecovery) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/reset-password" replace />} />
        </Routes>
      </Suspense>
    )
  }

  if (isAuthenticated) {
    return <AuthenticatedApp />
  }

  if (isGuest) {
    return <GuestApp />
  }

  return <UnauthenticatedApp />
}

// React Query cache persistence — write every query result to
// localStorage, hydrate from disk on app boot. The user sees the LAST
// known data instantly while fresh data loads in the background
// (stale-while-revalidate). `buster` is the fixed QUERY_CACHE_VERSION tag
// below — NOT the bundle version (see the note on it). The persister
// (lib/queryPersist.ts) binds the saved blob to the signed-in account, and
// PERSIST_SKIP_PREFIXES keep volatile / auth / subscription / admin queries
// off disk (review 2026-10-13).
const _persister = createUserBoundPersister(
  typeof window !== "undefined" ? window.localStorage : undefined,
);
/** Bump when a cached API response shape changes incompatibly (see buster). */
export const QUERY_CACHE_VERSION = "qc-2026-10-09";
const _persistOpts = {
  persister: _persister,
  // queryClient's default gcTime is kept >= this (lib/queryClient.ts).
  maxAge: QUERY_CACHE_MAX_AGE_MS,
  // A fixed tag, not the build's git SHA (2026-10-09): the SHA changes on
  // every deploy — 100+ a week — and a changed buster throws the whole saved
  // cache away, so nearly every app open started cold and every card showed
  // a skeleton while ~30 requests queued for the API's DB connections.
  // Saved data is only a first paint; each query still refetches in the
  // background. BUMP THIS when a response shape changes in a way an older
  // cached copy would break the screen.
  buster: QUERY_CACHE_VERSION,
  dehydrateOptions: {
    shouldDehydrateQuery: shouldPersistQuery,
  },
};

export default function App() {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={_persistOpts}>
      <RestOfApp />
    </PersistQueryClientProvider>
  );
}

function RestOfApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <GuestModeProvider>
            <GamificationProvider>
              <BrowserRouter>
                <PageTrackingHandler />
                <DeepLinkHandler />
                <ReferralCaptureHandler />
                <a
                  href="#main-content"
                  className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-black focus:rounded-full focus:text-sm focus:font-medium"
                >
                  Skip to content
                </a>
                <OfflineBanner />
                <MinimumVersionGate />
                <VersionUpdateBanner />
                {/* Promotes App Store install on public marketing routes
                    (Landing, FAQ, Support, Terms, Privacy, About, Why,
                    Login, Reset). Hidden inside the Capacitor app and on
                    in-app routes (/app, /profile, etc.) — see component
                    for the visible-path whitelist. */}
                <DownloadAppBanner />
                {/* UK/EU visitors (device time zone) choose about analytics
                    cookies; sits above the download banner until answered.
                    Re-opened from Privacy → Cookie settings. */}
                <AnalyticsConsentBanner />
                <ActiveWorkoutSticky />
                <ActiveMissionBar />
                <Toaster />
                <OfflineSyncDropListener />
                <UploadingOverlay />
                <ErrorBoundary fallbackLevel="page" onCaught={markAppShellErrored}>
                  <Router />
                  {/* Tells Capgo the bundle booted — only mounts when the
                      shell above rendered without crashing. */}
                  <AppReadySignal />
                </ErrorBoundary>
              </BrowserRouter>
            </GamificationProvider>
          </GuestModeProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
